import type {
  TaggedEnum,
  TaggedEnumTypeDescriptor,
  TaggedEnumValueDescriptor,
} from '@suntex.core/types';
import { mapValues, toRecord } from '@suntex.core/utils';

import { InvalidEnumError } from './errors';

export type TaggedEnumFromDescriptor<Desc extends TaggedEnumTypeDescriptor> =
  Desc extends TaggedEnumTypeDescriptor<infer T> ? T : never;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type SlugFor<S extends Pick<StaticContext<any, any, any>, 'describe'>> =
  ReturnType<S['describe']>['slug'];

export function enumValues<TEnum extends number>(enumType: {
  [key in string]: TEnum | string;
}): TEnum[] {
  return Object.values(enumType).filter((value) => typeof value === 'number');
}

export type EnumOption<Value> = {
  key: Value;
  value: Value;
  label: string;
};

type TaggedEnumValueDescriptorFromDescriptor<
  Desc extends TaggedEnumTypeDescriptor,
> =
  Desc extends TaggedEnumTypeDescriptor<TaggedEnum<string>, infer U>
    ? U
    : never;

type StaticContext<
  Desc extends TaggedEnumTypeDescriptor,
  Value extends TaggedEnumFromDescriptor<Desc>,
  ValueDesc extends TaggedEnumValueDescriptorFromDescriptor<Desc>,
> = {
  all: () => readonly Value[];
  from: (value: string | number) => Value;
  fromSlug: (slug: string) => Value;
  fromOptional: (
    value: string | number | null | undefined,
  ) => Value | undefined;
  fromOptionalSlug: (slug: string | null | undefined) => Value | undefined;
  describe: (value: Value) => ValueDesc;
  describeAll: (subset?: readonly Value[]) => readonly ValueDesc[];
  listOptions: (values?: readonly Value[]) => EnumOption<Value>[];
  throwInvalid: (key: string | number) => never;
};

/**
 * The naming convention for static contexts uses a '$' sigil with the type name, e.g. `$MyTaggedEnum` for `MyTaggedEnum`
 */
export function buildStaticContext<
  Desc extends TaggedEnumTypeDescriptor,
  Value extends TaggedEnumFromDescriptor<Desc>,
  ValueDesc extends TaggedEnumValueDescriptorFromDescriptor<Desc>,
>(enumTypeDesc: Desc): StaticContext<Desc, Value, ValueDesc> {
  const ctx = {
    all: () =>
      Object.values(enumTypeDesc).map(
        (desc) => desc.value as Value,
      ) as readonly Value[],
    from: (value: string | number) =>
      asTaggedEnum(enumTypeDesc, value) as Value,
    fromOptional: (value: string | number | null | undefined) => {
      if (value === null || value === undefined) return undefined;
      try {
        return ctx.from(value);
      } catch {
        return undefined;
      }
    },
    fromSlug: (slug: string) => {
      return enumDescBySlug(enumTypeDesc, slug).value as Value;
    },
    fromOptionalSlug: (slug: string | null | undefined) => {
      if (slug === null || slug === undefined) return undefined;
      try {
        return ctx.fromSlug(slug);
      } catch {
        return undefined;
      }
    },
    describe: (value: Value) => {
      return enumDescByValue(enumTypeDesc, value) as ValueDesc;
    },
    describeAll: (subset?: readonly Value[]) => {
      return enumDescForValues(enumTypeDesc, subset) as readonly ValueDesc[];
    },
    listOptions: (values?: readonly Value[]): EnumOption<Value>[] => {
      return enumDescForValues(enumTypeDesc, values).map((desc) => ({
        key: desc.value as Value,
        value: desc.value as Value,
        label: desc.label ?? desc.slug ?? `${desc.value}`,
      }));
    },
    throwInvalid: (key: string | number) => {
      throw new InvalidEnumError(enumTypeDesc, key);
    },
  } as const;
  ctx satisfies StaticContext<Desc, Value, ValueDesc>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ctx satisfies Record<string, (this: void, ...args: any[]) => any>;
  return ctx;
}

export function asTaggedEnum<Desc extends TaggedEnumTypeDescriptor>(
  enumTypeDesc: Desc,
  value: string | number,
): TaggedEnumFromDescriptor<Desc> {
  return (
    enumDescByValue(enumTypeDesc, value) satisfies TaggedEnumValueDescriptor
  ).value as TaggedEnumFromDescriptor<Desc>;
}

export function enumDescByValue<Desc extends TaggedEnumTypeDescriptor>(
  enumTypeDesc: Desc,
  value: string | number,
): TaggedEnumValueDescriptorFromDescriptor<Desc> {
  value = Number(value);
  const desc = Object.values(enumTypeDesc).find((desc) => desc.value === value);

  if (!desc) {
    throw new InvalidEnumError(enumTypeDesc, value);
  }

  return desc as TaggedEnumValueDescriptorFromDescriptor<Desc>;
}

export function enumDescBySlug<Desc extends TaggedEnumTypeDescriptor>(
  enumTypeDesc: Desc,
  slug: string,
): TaggedEnumValueDescriptorFromDescriptor<Desc> {
  const desc = Object.values(enumTypeDesc).find((desc) => desc.slug === slug);

  if (!desc) {
    throw new InvalidEnumError(enumTypeDesc, slug);
  }

  return desc as TaggedEnumValueDescriptorFromDescriptor<Desc>;
}

export function enumDescForValues<Desc extends TaggedEnumTypeDescriptor>(
  enumTypeDesc: Desc,
  values?: readonly (string | number)[],
): readonly TaggedEnumValueDescriptorFromDescriptor<Desc>[] {
  return values
    ? values.map((v) => enumDescByValue(enumTypeDesc, v))
    : Object.values(enumTypeDesc).map(
        (desc) => desc as TaggedEnumValueDescriptorFromDescriptor<Desc>,
      );
}

export function enumToRecord<
  Desc extends TaggedEnumTypeDescriptor,
  ValueDesc extends TaggedEnumValueDescriptorFromDescriptor<Desc>,
  K extends keyof ValueDesc & string,
  V extends keyof ValueDesc & string,
>(enumTypeDesc: Desc, keyProp: K, valueProp: V) {
  return mapValues(
    toRecord(Object.values(enumTypeDesc) as ValueDesc[], keyProp),
    (desc) => (desc as ValueDesc)[valueProp],
  ) as ValueDesc[K] extends string
    ? Record<ValueDesc[K], ValueDesc[V]>
    : Record<string, ValueDesc[V]>;
}
